import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';
import { PathkitLogo } from './PathkitLogo';

export function TermsOfService() {
  const lastUpdated = "December 7, 2024";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <PathkitLogo />
              <span className="text-xl">Pathkit</span>
            </div>
            <Button variant="ghost" onClick={() => window.location.href = '/'}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg border border-gray-200 p-8 md:p-12">
          <h1 className="text-4xl mb-4">Terms of Service</h1>
          <p className="text-gray-600 mb-8">Last Updated: {lastUpdated}</p>

          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl mb-4">1. Agreement to Terms</h2>
              <p className="text-gray-700 mb-4">
                By accessing or using Pathkit ("Service," "Platform," "we," "our," or "us"), you agree to be bound by 
                these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.
              </p>
              <p className="text-gray-700">
                These Terms apply to all users, including consulting firms, consultants, and client company administrators.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">2. Description of Service</h2>
              <p className="text-gray-700 mb-4">
                Pathkit is a SaaS platform designed for consultants to streamline service desk implementations and project scoping. The Service provides:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Template-based service desk configuration builder</li>
                <li>Multi-tenant consulting firm and client company management</li>
                <li>AI-powered discovery and planning assistance</li>
                <li>Atlassian Forge integration for Jira/Confluence</li>
                <li>Documentation generation and export capabilities</li>
                <li>Collaboration and communication tools</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">3. User Accounts</h2>
              
              <h3 className="text-xl mb-3 mt-6">3.1 Account Creation</h3>
              <p className="text-gray-700 mb-4">You must:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Provide accurate, current, and complete information</li>
                <li>Be at least 18 years of age</li>
                <li>Have authority to bind your organization to these Terms</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized access</li>
              </ul>

              <h3 className="text-xl mb-3 mt-6">3.2 Account Types</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Consulting Firm Admin:</strong> Full control over firm settings and client companies</li>
                <li><strong>Consultant:</strong> Access to assigned client companies and projects</li>
                <li><strong>Client Company Admin:</strong> Management of their company's configurations</li>
                <li><strong>System Admin:</strong> Platform-wide administrative access (Pathkit staff only)</li>
              </ul>

              <h3 className="text-xl mb-3 mt-6">3.3 Account Termination</h3>
              <p className="text-gray-700">
                We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent, 
                abusive, or illegal activities.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">4. Subscription and Billing</h2>
              
              <h3 className="text-xl mb-3 mt-6">4.1 Subscription Plans</h3>
              <p className="text-gray-700 mb-4">
                Pathkit offers multiple subscription tiers with different features and pricing. Current plans include:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Starter:</strong> For individual consultants</li>
                <li><strong>Professional:</strong> For small consulting firms</li>
                <li><strong>Enterprise:</strong> For large organizations with custom needs</li>
              </ul>

              <h3 className="text-xl mb-3 mt-6">4.2 Billing Terms</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Subscriptions are billed monthly or annually in advance</li>
                <li>Prices are subject to change with 30 days notice</li>
                <li>All fees are non-refundable except as required by law</li>
                <li>Failed payments may result in service suspension</li>
                <li>You're responsible for applicable taxes</li>
              </ul>

              <h3 className="text-xl mb-3 mt-6">4.3 Free Trial</h3>
              <p className="text-gray-700">
                Free trial periods are offered at our discretion. We may require payment information during signup. 
                If you don't cancel before the trial ends, you'll be charged for the selected plan.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">5. Acceptable Use</h2>
              
              <h3 className="text-xl mb-3 mt-6">5.1 You May:</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Use the Service for lawful business purposes</li>
                <li>Create and share service desk configurations</li>
                <li>Collaborate with team members and clients</li>
                <li>Export your data at any time</li>
                <li>Connect with Atlassian products via our Forge integration</li>
              </ul>

              <h3 className="text-xl mb-3 mt-6">5.2 You May NOT:</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Share your account credentials with unauthorized users</li>
                <li>Attempt to breach security or authentication measures</li>
                <li>Reverse engineer, decompile, or disassemble the Service</li>
                <li>Use the Service to transmit malware or harmful code</li>
                <li>Scrape, crawl, or spider the Service</li>
                <li>Interfere with other users' access to the Service</li>
                <li>Use the Service to violate any laws or regulations</li>
                <li>Resell or redistribute the Service without authorization</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">6. Intellectual Property</h2>
              
              <h3 className="text-xl mb-3 mt-6">6.1 Our IP</h3>
              <p className="text-gray-700 mb-4">
                Pathkit and all related trademarks, logos, and service marks are owned by us. The Service's design, 
                features, and underlying technology are protected by copyright, trademark, and other intellectual property laws.
              </p>

              <h3 className="text-xl mb-3 mt-6">6.2 Your Content</h3>
              <p className="text-gray-700 mb-4">
                You retain ownership of all configurations, templates, documentation, and other content you create 
                ("Your Content"). By using the Service, you grant us a license to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Store, process, and transmit Your Content</li>
                <li>Display Your Content to users you authorize</li>
                <li>Create backups and copies for operational purposes</li>
                <li>Use anonymized, aggregated data for service improvement</li>
              </ul>

              <h3 className="text-xl mb-3 mt-6">6.3 Templates</h3>
              <p className="text-gray-700">
                Templates provided by Pathkit are licensed for your use within the platform. You may customize templates 
                for your projects but may not redistribute Pathkit templates outside the platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">7. Data and Privacy</h2>
              <p className="text-gray-700 mb-4">
                Our collection and use of personal information is described in our Privacy Policy. Key points:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>We implement industry-standard security measures</li>
                <li>Your data is encrypted in transit and at rest</li>
                <li>Multi-tenant isolation prevents data leakage</li>
                <li>You can export your data at any time</li>
                <li>We comply with GDPR and other privacy regulations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">8. Third-Party Integrations</h2>
              
              <h3 className="text-xl mb-3 mt-6">8.1 Atlassian Integration</h3>
              <p className="text-gray-700 mb-4">
                Our Forge app integrates with Atlassian products (Jira, Confluence). By using this integration:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>You authorize us to access your Atlassian data as necessary</li>
                <li>You agree to Atlassian's terms of service</li>
                <li>We're not responsible for Atlassian service availability</li>
                <li>Integration features depend on your Atlassian subscription</li>
              </ul>

              <h3 className="text-xl mb-3 mt-6">8.2 Other Third Parties</h3>
              <p className="text-gray-700">
                We use third-party services for hosting (Supabase), payments (Stripe), and email (Resend). 
                These providers have their own terms and privacy policies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">9. Warranties and Disclaimers</h2>
              <p className="text-gray-700 mb-4">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, 
                INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Warranties of merchantability or fitness for a particular purpose</li>
                <li>Warranties of non-infringement</li>
                <li>Warranties that the Service will be uninterrupted or error-free</li>
                <li>Warranties regarding the accuracy of results or content</li>
              </ul>
              <p className="text-gray-700">
                We strive for 99.9% uptime but do not guarantee continuous, uninterrupted access to the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">10. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, PATHKIT SHALL NOT BE LIABLE FOR:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, revenue, data, or business opportunities</li>
                <li>Service interruptions or data loss</li>
                <li>Damages resulting from third-party services or integrations</li>
              </ul>
              <p className="text-gray-700">
                Our total liability for any claims related to the Service is limited to the amount you paid us in the 
                12 months preceding the claim.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">11. Indemnification</h2>
              <p className="text-gray-700">
                You agree to indemnify and hold harmless Pathkit from any claims, damages, losses, and expenses 
                (including legal fees) arising from:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Your use of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any rights of another party</li>
                <li>Your Content or activities on the platform</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">12. Modifications to Service</h2>
              <p className="text-gray-700">
                We reserve the right to modify, suspend, or discontinue any part of the Service at any time. We'll 
                provide reasonable notice for significant changes, but we're not liable for any modification, 
                suspension, or discontinuation.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">13. Changes to Terms</h2>
              <p className="text-gray-700">
                We may revise these Terms at any time. Material changes will be announced via email or platform notification. 
                Continued use after changes constitutes acceptance. If you disagree with changes, you must stop using the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">14. Governing Law</h2>
              <p className="text-gray-700">
                These Terms are governed by and construed in accordance with applicable laws, without regard to conflict 
                of law principles. Any disputes shall be resolved through binding arbitration or in courts of competent jurisdiction.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">15. Severability</h2>
              <p className="text-gray-700">
                If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited 
                or eliminated to the minimum extent necessary, and the remaining provisions will remain in full force.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">16. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                For questions about these Terms:
              </p>
              <ul className="list-none mb-4 text-gray-700 space-y-2">
                <li><strong>Email:</strong> legal@pathkit.pro</li>
                <li><strong>Support:</strong> support@pathkit.pro</li>
                <li><strong>Address:</strong> Pathkit Legal Team</li>
              </ul>
            </section>

            <section className="mb-8">
              <p className="text-gray-700 italic">
                By using Pathkit, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}